import {useCallback, useEffect, useRef} from 'react';

import {MAX_DRAWER_WIDTH, MIN_DRAWER_WIDTH} from './constants';
import {useViewportSize} from './useViewportSize';
import {useWBAgentContextStore} from './WBAgentContextStore';

const computeMaxAllowedWidth = (viewportWidth: number) =>
  Math.min(MAX_DRAWER_WIDTH, viewportWidth * 0.75);

export function useDrawerResize() {
  const dragging = useRef(false);
  const desiredWidth = useWBAgentContextStore(s => s.drawerWidth);
  const setWidth = useWBAgentContextStore(s => s.setDrawerWidth);
  const {width: viewportWidth} = useViewportSize();

  const effectiveWidth = Math.max(
    MIN_DRAWER_WIDTH,
    Math.min(desiredWidth, computeMaxAllowedWidth(viewportWidth))
  );

  const onMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    dragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, []);

  const onMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!dragging.current) {
        return;
      }
      const newWidth = Math.min(
        computeMaxAllowedWidth(window.innerWidth),
        Math.max(MIN_DRAWER_WIDTH, window.innerWidth - e.clientX)
      );
      setWidth(newWidth);
    },
    [setWidth]
  );

  const onMouseUp = useCallback(() => {
    dragging.current = false;
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  }, []);

  useEffect(() => {
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
  }, [onMouseMove, onMouseUp]);

  return {width: effectiveWidth, onMouseDown};
}
